import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { TableWrapper } from '@components/Table/TableWrapper';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import { useMenu } from '@hooks/useMenu';
import useMessage from '@hooks/useMessage';
import { useModalWindow } from '@hooks/useModalWindow';
import { EnumSortDirection } from '@hooks/useRequest';
import { AtividadeModal } from '@mod/Atividade/AtividadeModal';
import { EnumEmpresa, useEmpresaCombo } from '@model/Empresa';
import IFatura from '@model/Fatura';
import { EnumFaturaStatus, useFaturaStatusCombo } from '@model/FaturaStatus';
import { useRemetenteWhatsappCombo } from '@model/RemetenteWhatsapp';
import { IFaturaFilter, useFaturaService } from '@services/FaturaService';
import React from 'react';
import { FaturaCancelarModal } from './FaturaCancelarModal';
import { FaturaFilter } from './FaturaFilter';
import { FaturaMenu } from './FaturaMenu';
import { FaturaReabrirModal } from './FaturaReabrirModal';
import { FaturaTable } from './FaturaTable';
import { FaturaDesfazerCancelamentoModal } from './FaturaDesfazerCancelamentoModal';

export type FaturaProps = {
}

export function Fatura (props: FaturaProps) {
    const data = useData<IFatura, IFaturaFilter>({
        filter: {
            faturaId: useField(),
            clienteNome: useField(),
            clienteCpf: useField(),
            clienteRemetenteWhatsapp: useField(),
            competencia: useField(),
            empresaId: useField(EnumEmpresa.None),
            faturaStatusId: useField(EnumFaturaStatus.None),
            tipo: useField()
        },

        sortField: {
            name: "faturaId",
            direction: EnumSortDirection.Desc
        }
    })

    const loading = useLoading();
    const message = useMessage();
    const faturaService = useFaturaService();
    const faturaDetalheModal = useModalWindow();
    const cancelarModal = useModalWindow();
    const reabrirModal = useModalWindow();
    const desfazerCancelamentoModal = useModalWindow();
    const atividadeModal = useModalWindow();
    const menu = useMenu();

    const empresaCombo = useEmpresaCombo();
    const faturaStatusCombo = useFaturaStatusCombo();
    const remetenteWhatsappCombo = useRemetenteWhatsappCombo();

    React.useEffect(() => {
        if (!loading.isLoading) {
            loading.start();
            faturaService.listar(data.requestedData)
                .then(data.handleResultResponse)
                .catch(message.showException)
                .finally(loading.stop);
}
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    useEffectOnce(() => {
        empresaCombo.loadOptions();
        faturaStatusCombo.loadOptions();
        remetenteWhatsappCombo.loadOptions();
    });

    return (
        <Main>
            <PageTitle>
                <PageTitleText text="Faturas" />
            </PageTitle>

            <PageContent>
                <FaturaFilter
                    filter={data.filter}
                    isLoading={loading.isLoading}
                    empresaCombo={empresaCombo}
                    faturaStatusCombo={faturaStatusCombo}
                    remetenteWhatsappCombo={remetenteWhatsappCombo}
                />
                
                <TableWrapper>
                    <FaturaTable 
                        menu={menu}
                        data={data}
                        isLoading={loading.isLoading}
                        faturaDetalheModal={faturaDetalheModal}
                    />
                </TableWrapper>
            </PageContent>

            {
                data.focusedRow !== undefined && (
                    <React.Fragment>
                        <FaturaMenu 
                            menu={menu}
                            fatura={data.focusedRow}
                            cancelarModal={cancelarModal}
                            reabrirModal={reabrirModal}
                            desfazerCancelamentoModal={desfazerCancelamentoModal}
                            atividadeModal={atividadeModal}
                        />

                        <FaturaCancelarModal 
                            modal={cancelarModal}
                            fatura={data.focusedRow}
                            onFaturaCancelada={data.handleUpdateFocusedRow}
                        />

                        <FaturaReabrirModal 
                            modal={reabrirModal}
                            fatura={data.focusedRow}
                            onFaturaReaberta={data.handleUpdateFocusedRow}
                        />

                        <FaturaDesfazerCancelamentoModal 
                            modal={desfazerCancelamentoModal}
                            fatura={data.focusedRow}
                            onFaturaCancelamentoDesfeito={data.handleUpdateFocusedRow}
                        />

                        <AtividadeModal 
                            modal={atividadeModal}
                            entidade='fatura'
                            chaves={{
                                faturaId: data.focusedRow.faturaId
                            }}
                        />
                    </React.Fragment>
                )
            }
        </Main>
    )
}